import { contentTypeFromFilename } from '../tools/contentTypeTranslator';
import { storeFile, getDownloadUrlByPath } from '../firebase/storageFunctions';

const MODULE_PREFIX = 'upload';

const ACTION_UPLOAD_FILE = 'uploadFile';
const ACTION_UPLOAD_REFLECTION_IMAGE = 'uploadReflectionImage';
const ACTION_FETCH_DOWNLOAD_URL = 'fetchDownloadUrl';

const MUTATION_START_UPLOAD = 'startUpload';
const MUTATION_UPLOAD_FINISHED = 'uploadFinished';
const MUTATION_UPLOAD_FAILED = 'uploadFailed';
const MUTATION_UPLOAD_PROGRESS_CHANGED = 'uploadProgressChanged';
const MUTATION_SET_DOWNLOAD_URL = 'setUploadUrl';
const MUTATION_RESET_UPLOAD_STATE = 'resetUploadState';

const GETTER_UPLOAD_IN_PROGRESS = 'uploadInProgress';
const GETTER_UPLOAD_PROGRESS_STATE = 'uploadProgressState';
const GETTER_UPLOADED_FILE = 'uploadedFile';
const GETTER_UPLOADED_FILE_DOWNLOAD_URL = 'uploadedFileDownloadUrl';
const GETTER_UPLOAD_SUCCESS = 'uploadSuccess';

export const onSnapshotForUploadStateChanged = (commit, snapshot) => {
  const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
  commit(MUTATION_UPLOAD_PROGRESS_CHANGED, progress);
};

const initialState = {
  uploadedFile: '',
  uploadedFileDownloadUrl: '',
  uploadInProgress: false,
  uploadProgressState: 0,
  uploadSuccess: false,
  bucketFilePath: '',
};

const getters = {
  [GETTER_UPLOAD_IN_PROGRESS]: state => state.uploadInProgress,
  [GETTER_UPLOAD_SUCCESS]: state => state.uploadSuccess,
  [GETTER_UPLOADED_FILE]: state => state.uploadedFile,
  [GETTER_UPLOADED_FILE_DOWNLOAD_URL]: state => state.uploadedFileDownloadUrl,
  [GETTER_UPLOAD_PROGRESS_STATE]: state => state.uploadProgressState,
};

const mutations = {
  [MUTATION_START_UPLOAD]: (state, bucketFilePath) => {
    state.uploadInProgress = true;
    state.bucketFilePath = bucketFilePath;
  },
  [MUTATION_UPLOAD_FINISHED]: (state, uploadedFile) => {
    state.uploadedFile = uploadedFile;
    state.uploadInProgress = false;
    state.uploadSuccess = true;
  },
  [MUTATION_UPLOAD_FAILED]: (state, error) => {
    state.uploadedFile = error;
    state.uploadInProgress = false;
    state.uploadSuccess = false;
  },
  [MUTATION_SET_DOWNLOAD_URL]: (state, url) => {
    state.uploadedFileDownloadUrl = url;
  },
  [MUTATION_UPLOAD_PROGRESS_CHANGED]: (state, uploadProgressState) => {
    state.uploadProgressState = uploadProgressState;
  },
  [MUTATION_RESET_UPLOAD_STATE]: (state) => {
    state.uploadProgressState = 0;
    state.uploadedFile = '';
    state.uploadedDownloadUrl = '';
    state.uploadInProgress = false;
    state.uploadSuccess = false;
    state.bucketFilePath = '';
  },
};

const actions = {
  [ACTION_UPLOAD_REFLECTION_IMAGE]: async ({ dispatch }, { file, title, reflectionId }) => {
    const { uuid } = file.upload;
    const path = `/reflections/${reflectionId}/${uuid}`;
    await dispatch(types.actions.uploadFile, { file, title, path }, { root: true });
  },
  [ACTION_UPLOAD_FILE]: async ({ commit, dispatch }, { file, title, path }) => {
    commit(MUTATION_START_UPLOAD, path);
    try {
      await storeFile(
        file.type || contentTypeFromFilename(file.upload.filename),
        file,
        title,
        path,
        (snapshot) => onSnapshotForUploadStateChanged(commit, snapshot),
      );
      commit(MUTATION_UPLOAD_FINISHED, file);
      await dispatch(types.actions.fetchDownloadUrl, {}, { root: true });
    } catch (error) {
      console.error(`Error while uploading file ${JSON.stringify(file)} with title ${title} at path ${path}`, error);
      commit(MUTATION_UPLOAD_FAILED, error);
    }
  },
  [ACTION_FETCH_DOWNLOAD_URL]: async ({ state, commit }) => {
    // TODO could be moved in a utilities package
    const callWithRetry = async (depth = 0) => {
      const wait = (ms) => new Promise((res) => setTimeout(res, ms));
      try {
        return await getDownloadUrlByPath(state.bucketFilePath);
      } catch (e) {
        if (depth > 7) {
          throw e;
        }
        await wait((2 ** depth) * 10);
        return callWithRetry(depth + 1);
      }
    };

    const downloadUrl = await callWithRetry();
    if (downloadUrl !== '') {
      commit(MUTATION_SET_DOWNLOAD_URL, downloadUrl);
    }
  },
};

export default {
  namespaced: true, state: initialState, actions, getters, mutations,
};

export const types = {
  actions: {
    uploadFile: `${MODULE_PREFIX}/${ACTION_UPLOAD_FILE}`,
    fetchDownloadUrl: `${MODULE_PREFIX}/${ACTION_FETCH_DOWNLOAD_URL}`,
    uploadReflectionImage: `${MODULE_PREFIX}/${ACTION_UPLOAD_REFLECTION_IMAGE}`,
  },
  mutations: {
    resetUploadState: `${MODULE_PREFIX}/${MUTATION_RESET_UPLOAD_STATE}`,
  },
  getters: {
    uploadInProgress: `${MODULE_PREFIX}/${GETTER_UPLOAD_IN_PROGRESS}`,
    uploadProgressState: `${MODULE_PREFIX}/${GETTER_UPLOAD_PROGRESS_STATE}`,
    uploadedFile: `${MODULE_PREFIX}/${GETTER_UPLOADED_FILE}`,
    uploadedFileDownloadUrl: `${MODULE_PREFIX}/${GETTER_UPLOADED_FILE_DOWNLOAD_URL}`,
    uploadSuccess: `${MODULE_PREFIX}/${GETTER_UPLOAD_SUCCESS}`,
  },
};
